import { useCallback } from "react";

import {
  ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL,
  ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS,
  ENTRY_DATA_LICENSEE_TABLE,
  ENTRY_DATA_LICENSEE_TABLE_KEYS,
  ENTRY_DATA_LICENSOR_TABLE,
  ENTRY_DATA_LICENSOR_TABLE_KEYS,
  ENTRY_DATA_RIGHTS_SCOPE_KEYS,
  ENTRY_DATA_SECTION_KEYS,
  ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS,
  VENTURE_FORMATION_TABLE_KEYS,
} from "@frontend/common";

import { DealFilingSection, LicenseeTableValue } from "@/types/deals.types";
import {
  DEFAULT_FOCUS_STATUS,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import { useTableCreatorPositionInfo } from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorTableReturn {
  handleClickLicenseeTableClearButton: () => void;
  handleKeyDownToClearLicenseeTable: (event: KeyboardEvent) => void;
}

export interface TableInfoProps {
  groupTableKey: DealFilingSection;
  tableKey: string | null;
  groupTableIndex: number;
  tableIndex: number;
}

export const useHandleMergeLicenseeTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const { groupTableKey, tableKey, groupTableIndex, tableIndex } =
    tablePropsInfo;

  const { changeCurrentHistory } = useTableCreatorCurrentHistory();

  const { info } = useTableCreatorPositionInfo();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();

  const handleClearTable = useCallback(() => {
    const targetLicenseeData =
      entryData[ENTRY_DATA_SECTION_KEYS.dealFiling]?.[
        ENTRY_DATA_LICENSEE_TABLE
      ];

    const clearDealPurposeTable = (): ArticleEntryData => {
      const additionalInformationTableData =
        entryData[ENTRY_DATA_SECTION_KEYS.dealFiling]?.[
          ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL
        ];
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealFiling]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealFiling],
          [ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL]: {
            ...additionalInformationTableData,
            [ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS.purposeOfTheDeal]: {
              ...additionalInformationTableData?.[
                ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS.purposeOfTheDeal
              ],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    const clearNewCompanyTable = (): ArticleEntryData => {
      const licensorTableData =
        entryData[ENTRY_DATA_SECTION_KEYS.dealFiling]?.[
          ENTRY_DATA_LICENSOR_TABLE
        ];
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealFiling]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealFiling],
          [ENTRY_DATA_LICENSOR_TABLE]: {
            ...licensorTableData,
            [ENTRY_DATA_LICENSOR_TABLE_KEYS.licensorName]: {
              ...licensorTableData?.[
                ENTRY_DATA_LICENSOR_TABLE_KEYS.licensorName
              ],
              value: [],
            },
            [ENTRY_DATA_LICENSOR_TABLE_KEYS.licensorResponsibilityRights]: {
              ...licensorTableData?.[
                ENTRY_DATA_LICENSOR_TABLE_KEYS.licensorResponsibilityRights
              ],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    const clearExistingCompanyTable = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealFiling]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealFiling],
          [ENTRY_DATA_LICENSEE_TABLE]: targetLicenseeData?.map(
            (tableInfo, tableInfoIndex): LicenseeTableValue => {
              if (tableInfoIndex === info.tableIndex) {
                const mergeData =
                  tableInfo?.[
                    ENTRY_DATA_LICENSEE_TABLE_KEYS.ventureFormationTerms
                  ];
                return {
                  ...tableInfo,
                  [ENTRY_DATA_LICENSEE_TABLE_KEYS.licenseeName]: {
                    ...tableInfo?.[ENTRY_DATA_LICENSEE_TABLE_KEYS.licenseeName],
                    value: [],
                  },
                  [ENTRY_DATA_LICENSEE_TABLE_KEYS.rightsScope]: tableInfo?.[
                    ENTRY_DATA_LICENSEE_TABLE_KEYS.rightsScope
                  ]?.map((groupShellInfo) => {
                    return {
                      ...groupShellInfo,
                      [ENTRY_DATA_RIGHTS_SCOPE_KEYS.drug]: {
                        ...groupShellInfo?.[ENTRY_DATA_RIGHTS_SCOPE_KEYS.drug],
                        value: [],
                      },
                      [ENTRY_DATA_RIGHTS_SCOPE_KEYS.exclusivity]: {
                        ...groupShellInfo?.[
                          ENTRY_DATA_RIGHTS_SCOPE_KEYS.exclusivity
                        ],
                        value: [],
                      },
                      [ENTRY_DATA_RIGHTS_SCOPE_KEYS.indication]: {
                        ...groupShellInfo?.[
                          ENTRY_DATA_RIGHTS_SCOPE_KEYS.indication
                        ],
                        value: [],
                      },
                      [ENTRY_DATA_RIGHTS_SCOPE_KEYS.territory]: {
                        ...groupShellInfo?.[
                          ENTRY_DATA_RIGHTS_SCOPE_KEYS.territory
                        ],
                        value: [],
                      },
                    };
                  }),
                  [ENTRY_DATA_LICENSEE_TABLE_KEYS.ventureFormationTerms]: {
                    ...mergeData,
                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.licenseeResponsibilityAndRightsVf]:
                      {
                        ...mergeData[
                          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                            .licenseeResponsibilityAndRightsVf
                        ],
                        value: [],
                      },
                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.initialInvestment]:
                      {
                        ...mergeData[
                          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                            .initialInvestment
                        ],
                        value: [],
                      },

                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.totalInvestmentVf]:
                      {
                        ...mergeData[
                          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                            .totalInvestmentVf
                        ],
                        value: [],
                      },
                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.disclosedPaymentsSumVf]:
                      {
                        ...mergeData[
                          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                            .disclosedPaymentsSumVf
                        ],
                        value: [],
                      },
                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.ownershipStructureDetails]:
                      {
                        ...mergeData[
                          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                            .ownershipStructureDetails
                        ],
                        value: [],
                      },
                    [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentInvestmentAndDetailVf]:
                      mergeData[
                        ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                          .contingentInvestmentAndDetailVf
                      ].map((subGroupShellInfo) => {
                        return {
                          [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentInvestmentVf]:
                            {
                              ...subGroupShellInfo[
                                ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                                  .contingentInvestmentVf
                              ],
                              value: [],
                            },
                          [ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentDetailVf]:
                            {
                              ...subGroupShellInfo[
                                ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
                                  .contingentDetailVf
                              ],
                              value: [],
                            },
                        };
                      }),
                  },
                };
              }
              return tableInfo;
            },
          ),
        },
      };
    };

    const clearAdditionalInformationTable = (): ArticleEntryData => {
      const additionalInformationTableData =
        entryData[ENTRY_DATA_SECTION_KEYS.dealFiling]?.[
          ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL
        ];
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealFiling]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealFiling],
          [ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL]: {
            ...additionalInformationTableData,
            [ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS.additionalInformation]:
              {
                ...additionalInformationTableData?.[
                  ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS
                    .additionalInformation
                ],
                value: [],
              },
          },
        },
      } as ArticleEntryData;
    };

    if (info.tableKey === VENTURE_FORMATION_TABLE_KEYS.dealPurpose) {
      setEntryData(clearDealPurposeTable());
      changeCurrentHistory({
        entryData: clearDealPurposeTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.tableKey === VENTURE_FORMATION_TABLE_KEYS.newCompany) {
      setEntryData(clearNewCompanyTable());
      changeCurrentHistory({
        entryData: clearNewCompanyTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.tableKey === VENTURE_FORMATION_TABLE_KEYS.existingCompany) {
      setEntryData(clearExistingCompanyTable());
      changeCurrentHistory({
        entryData: clearExistingCompanyTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }

    if (info.tableKey === VENTURE_FORMATION_TABLE_KEYS.additionalInformation) {
      setEntryData(clearAdditionalInformationTable());
      changeCurrentHistory({
        entryData: clearAdditionalInformationTable(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeFocusStatus(DEFAULT_FOCUS_STATUS);
    }
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    entryData,
    groupTableIndex,
    groupTableKey,
    info.tableIndex,
    info.tableKey,
    setEntryData,
    tableIndex,
    tableKey,
  ]);

  const handleClickLicenseeTableClearButton = () => {
    if (focusStatus.type === "clear") handleClearTable();
  };

  const handleKeyDownToClearLicenseeTable = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearTable();
      }
      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    },
    [focusStatus.type, changeValidatedInfo, handleClearTable],
  );

  return {
    handleClickLicenseeTableClearButton,
    handleKeyDownToClearLicenseeTable,
  };
};
